import { useMemo } from "react";
import { queries } from "../../shared/queries";
import { buildUserGroupMemberships } from "../../shared/rbac/grantQueries";
import {
  buildMemberGroupMap,
  type MemberGroupMap,
} from "../lib/memberGroupMap";
import { DEFERRED, useQuery } from "./useQuery";

export function useGroupMemberships(options?: { enabled?: boolean }) {
  const { enabled = true } = options ?? {};
  const [groupMemberships] = useQuery(
    enabled ? queries.selectableUserGroups() : undefined,
    DEFERRED
  );

  const { memberUserIds, memberCounts, userGroupIds } = useMemo(() => {
    const muIds = new Map<string, string[]>();
    for (const group of groupMemberships ?? []) {
      const activeIds: string[] = [];
      for (const r of group.roleGrantsAsScope) {
        if (r.userId && !r.user?.deactivated) activeIds.push(r.userId);
      }
      muIds.set(group.id, [...new Set(activeIds)]);
    }

    const mc = new Map([...muIds].map(([id, ids]) => [id, ids.length]));

    const flatGrants = (groupMemberships ?? []).flatMap((g) =>
      g.roleGrantsAsScope.map((r) => ({ userId: r.userId, scopeId: g.id }))
    );

    return {
      memberUserIds: muIds,
      memberCounts: mc,
      userGroupIds: buildUserGroupMemberships(flatGrants),
    };
  }, [groupMemberships]);

  const memberGroupMap: MemberGroupMap = useMemo(
    () => buildMemberGroupMap(groupMemberships ?? []),
    [groupMemberships]
  );

  return { memberCounts, memberUserIds, memberGroupMap, userGroupIds };
}
